import { useEffect, useRef, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import ButtonLoading from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { PATH_CONSTANT } from 'constant/PathConstant';
import {
  useCheckCubeCopyData,
  useCopyCubeDefinition,
  useGetCubeDefinitionDetails,
  useRetryCubeCopyData,
  useGetDropdownsCubePanel,
} from 'services/v1/SystemTenant/AROCube/CubeBuilderService';
import { AutoCompleteItem } from 'types/api/Common/AutoCompleteTypes';
import {
  CubeDefinitionItem,
  RequestCopyCubeDefinition,
  ResponseCopyCubeDefinition,
} from 'types/api/SystemTenant/AROCube/CubeBuilderTypes';
import { CubeCurrentStructure } from './CubeCurrentStructure';

type CopyCubeFormValues = Omit<RequestCopyCubeDefinition, 'copyData' | 'copyDataTenantId' | 'subjectId'> & {
  cubeName: string;
};

const initialValues: CopyCubeFormValues = {
  sourceCubeId: '',
  cubeName: '',
  memberId: '',
  cubeLayerSetId: '',
  timePeriodSetId: '',
};

const validationSchema = Yup.object({
  sourceCubeId: Yup.string().required('Source Cube is required'),
  cubeName: Yup.string().trim().max(255, 'Cube Name must be at most 255 characters'),
  memberId: Yup.string().required('Main Dimension (KTree) is required'),
  cubeLayerSetId: Yup.string().required('Layer Set is required'),
  timePeriodSetId: Yup.string().required('Time Period Set is required'),
});

interface ModalCopyCubeDefinitionProps {
  cubes: CubeDefinitionItem[];
  onClose: () => void;
}

export function ModalCopyCubeDefinition({ cubes, onClose }: ModalCopyCubeDefinitionProps) {
  const navigate = useNavigate();
  const copy = useCopyCubeDefinition();
  const retryData = useRetryCubeCopyData();
  const isSaving = copy.isLoading || retryData.isLoading;
  const copyInFlight = useRef(false);
  const [copyData, setCopyData] = useState(false);
  const [copiedCube, setCopiedCube] = useState<ResponseCopyCubeDefinition | null>(null);

  const formik = useFormik<CopyCubeFormValues>({
    initialValues,
    validationSchema,
    validateOnMount: true,
    onSubmit: async (values) => {
      if (copyInFlight.current || copiedCube || !canCopy || !isDataCheckReady) return;

      copyInFlight.current = true;
      try {
        const { data } = await copy.mutateAsync({
          ...values,
          subjectId: sourceSubjectId,
          cubeName: values.cubeName.trim(),
          copyData,
          ...(copyData ? { copyDataTenantId: dataCheckResult?.tenantId } : {}),
        });
        showResult(data);
      } catch {
        // The mutation error is shown in the form, preserving selections for correction.
      } finally {
        copyInFlight.current = false;
      }
    },
  });

  const sourceDetails = useGetCubeDefinitionDetails(formik.values.sourceCubeId || undefined);
  const source =
    sourceDetails.isSuccess && sourceDetails.data?.data.id === formik.values.sourceCubeId
      ? sourceDetails.data.data
      : undefined;
  const sourceSubjectId = source?.subject?.id?.toString() || '';
  const sourcePending = Boolean(formik.values.sourceCubeId) && sourceDetails.isFetching;
  const dropdowns = useGetDropdownsCubePanel(sourceSubjectId || undefined);
  const parametersDisabled = !sourceSubjectId || sourcePending || dropdowns.isFetching || dropdowns.isError;
  const fields: {
    name: Exclude<keyof CopyCubeFormValues, 'cubeName'>;
    label: string;
    description: string;
    options: AutoCompleteItem[];
    loading?: boolean;
    disabled?: boolean;
  }[] = [
    {
      name: 'sourceCubeId',
      label: 'Source Cube',
      description: 'Select the cube to copy. The copied cube will use the same subject as the source.',
      options: cubes.map((cube) => ({
        value: cube.id,
        label: `${cube.cubeCode} · ${cube.cubeName} · Subject: ${cube.subjectDisplayName}`,
      })),
    },
    {
      name: 'memberId',
      label: 'Main Dimension (KTree)',
      description: 'Select the KTree definition to use as the main dimension of the copied cube.',
      options: dropdowns.data?.data.member || [],
      loading: dropdowns.isFetching,
      disabled: parametersDisabled,
    },
    {
      name: 'cubeLayerSetId',
      label: 'Layer Set',
      description: 'Select the set of layers to use for the copied cube.',
      options: dropdowns.data?.data.layerSet || [],
      loading: dropdowns.isFetching,
      disabled: parametersDisabled,
    },
    {
      name: 'timePeriodSetId',
      label: 'Time Period Set',
      description: 'Select the time period set that defines the periods available in the copied cube.',
      options: dropdowns.data?.data.timePeriod || [],
      loading: dropdowns.isFetching,
      disabled: parametersDisabled,
    },
  ];
  const canCopy = fields.every(
    (field) => !field.disabled && field.options.some((option) => option.value.toString() === formik.values[field.name])
  );

  const checkPayload = {
    ...formik.values,
    subjectId: sourceSubjectId,
    cubeName: formik.values.cubeName.trim(),
  };
  const checkPayloadKey = JSON.stringify(checkPayload);
  const [debouncedCheckKey, setDebouncedCheckKey] = useState(checkPayloadKey);
  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedCheckKey(checkPayloadKey), 300);
    return () => clearTimeout(timeout);
  }, [checkPayloadKey]);

  const canCheckData = canCopy && formik.isValid;
  const isCurrentCheck = checkPayloadKey === debouncedCheckKey;
  const dataCheck = useCheckCubeCopyData(
    checkPayload,
    copyData && canCheckData && isCurrentCheck && !isSaving && !copiedCube
  );
  const isCheckingData = !isCurrentCheck || dataCheck.isFetching || dataCheck.isLoading || dataCheck.isIdle;
  const dataCheckResult = canCheckData && !isCheckingData && dataCheck.isSuccess ? dataCheck.data?.data : undefined;
  const isDataCheckReady = !copyData || Boolean(dataCheckResult?.hasMatchingStructure);

  const handleChange = (name: Exclude<keyof CopyCubeFormValues, 'cubeName'>, option: AutoCompleteItem | null) => {
    copy.reset();
    const value = option?.value.toString() || '';
    if (name === 'sourceCubeId') {
      const source = cubes.find((cube) => cube.id === value);
      formik.setValues({
        ...formik.values,
        sourceCubeId: value,
        cubeName: source ? `${source.cubeName.slice(0, 250)} Copy` : '',
        memberId: '',
        cubeLayerSetId: '',
        timePeriodSetId: '',
      });
    } else {
      formik.setFieldValue(name, value);
    }
  };

  const showResult = (data: ResponseCopyCubeDefinition) => {
    if (
      data.deploymentStatus === 'INCOMPLETE' ||
      data.dataCopy?.status === 'INCOMPLETE' ||
      data.dataCopy?.status === 'COMPLETED'
    ) {
      setCopiedCube(data);
      return;
    }
    toast.success('Cube copied successfully.');
    onClose();
  };

  const handleRetryData = async () => {
    if (!copiedCube || isSaving || copyInFlight.current) return;
    copyInFlight.current = true;
    try {
      const { data } = await retryData.mutateAsync(copiedCube.cubeDefinitionId);
      showResult(data);
    } catch {
      // Keep the created cube identity so recovery never creates another copy.
    } finally {
      copyInFlight.current = false;
    }
  };

  const handleClose = () => {
    if (!isSaving) onClose();
  };

  const handleOpenCopiedCube = () => {
    if (!copiedCube) return;
    navigate(
      PATH_CONSTANT.SYSTEM.CUBE.CUBE_DEFINITION_EDIT.replace(':subjectId', encodeURIComponent(sourceSubjectId)).replace(
        ':cubeId',
        encodeURIComponent(copiedCube.cubeDefinitionId)
      )
    );
    onClose();
  };

  const renderDropdownField = (field: (typeof fields)[number]) => (
    <Stack key={field.name} spacing={0.75}>
      <Typography component='label' htmlFor={`copy-cube-${field.name}`} variant='input-label' fontWeight='bold'>
        {field.label}
      </Typography>
      <Typography id={`copy-cube-${field.name}-description`} variant='input-label-gray' component='p'>
        {field.description}
      </Typography>
      <Autocomplete
        id={`copy-cube-${field.name}`}
        size='small'
        fullWidth
        options={[...field.options].sort((a, b) =>
          a.label.localeCompare(b.label, 'en', { numeric: true, sensitivity: 'base' })
        )}
        value={field.options.find((option) => option.value.toString() === formik.values[field.name]) || null}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) => option.value.toString() === value.value.toString()}
        onChange={(_event, option) => handleChange(field.name, option)}
        disabled={copy.isLoading || field.disabled}
        loading={field.loading}
        renderInput={(params) => (
          <TextField
            {...params}
            name={field.name}
            placeholder='Select an option'
            required
            onBlur={formik.handleBlur}
            error={Boolean(formik.touched[field.name] && formik.errors[field.name])}
            helperText={formik.touched[field.name] && formik.errors[field.name]}
            inputProps={{
              ...params.inputProps,
              'aria-describedby': `copy-cube-${field.name}-description${
                formik.touched[field.name] && formik.errors[field.name] ? ` ${params.id}-helper-text` : ''
              }`,
            }}
          />
        )}
      />
    </Stack>
  );

  return (
    <Dialog open onClose={handleClose} maxWidth='sm' fullWidth aria-labelledby='copy-cube-title'>
      <Stack component='form' onSubmit={formik.handleSubmit} noValidate sx={{ minHeight: 0 }}>
        <Stack direction='row' justifyContent='space-between' alignItems='center' sx={{ px: 2, py: 1.5 }}>
          <Typography id='copy-cube-title' variant='body1' component='h2' fontWeight='bold' sx={{ color: '#3B4797' }}>
            Copy a Cube
          </Typography>
          <IconButton aria-label='Close' onClick={handleClose} disabled={isSaving}>
            <HighlightOffRoundedIcon sx={{ color: '#98A2AE' }} />
          </IconButton>
        </Stack>

        <DialogContent
          sx={{
            bgcolor: '#FBFBFB',
            px: 3,
            py: 3,
            borderTop: '1px solid #E3EBF6',
            borderBottom: '1px solid #E3EBF6',
          }}
        >
          <Stack spacing={2.5}>
            {copiedCube ? (
              <Stack spacing={1}>
                <Alert
                  severity={
                    copiedCube.deploymentStatus === 'INCOMPLETE' || copiedCube.dataCopy?.status === 'INCOMPLETE'
                      ? 'warning'
                      : 'success'
                  }
                >
                  <Typography variant='body2' fontWeight='bold'>
                    {copiedCube.cubeCode} · {copiedCube.cubeName}
                  </Typography>
                  {copiedCube.deploymentStatus === 'INCOMPLETE'
                    ? copiedCube.dataCopy?.status === 'INCOMPLETE'
                      ? 'The cube was created, but deployment and data copying need to finish. Retry below to continue with this cube.'
                      : 'The cube was copied, but tenant deployment is incomplete. Open the copied cube and save it to retry deployment. Do not submit another copy.'
                    : copiedCube.dataCopy?.message || copiedCube.message}
                  {copiedCube.dataCopy && copiedCube.dataCopy.status !== 'NOT_REQUESTED' && (
                    <Typography variant='body2' sx={{ mt: 0.5 }}>
                      {copiedCube.dataCopy.copiedRows} data rows and {copiedCube.dataCopy.copiedSpecialValues}{' '}
                      Base/Initial settings copied in DEV.
                    </Typography>
                  )}
                </Alert>
                {copiedCube.dataCopy?.warnings?.map((warning, index) => (
                  <Alert key={index} severity='warning'>
                    {warning}
                  </Alert>
                ))}
                {retryData.isError && (
                  <Alert severity='error'>
                    {retryData.error?.response?.data?.message || 'Unable to resume data copying. Try again.'}
                  </Alert>
                )}
              </Stack>
            ) : (
              <>
                {copy.isError && (
                  <Alert severity='error'>
                    {copy.error?.response?.data?.message || 'Unable to copy the cube. Please try again.'}
                  </Alert>
                )}
                {sourcePending && (
                  <Stack direction='row' spacing={1} alignItems='center' role='status'>
                    <CircularProgress size={16} aria-label='Loading source cube subject' />
                    <Typography variant='body2'>Loading source cube subject...</Typography>
                  </Stack>
                )}
                {sourceDetails.isSuccess && !sourcePending && !sourceSubjectId && (
                  <Alert severity='error'>The selected source cube has no subject. Choose another source cube.</Alert>
                )}
                {(sourceDetails.isError || dropdowns.isError) && (
                  <Alert
                    severity='error'
                    action={
                      <Button
                        color='inherit'
                        size='small'
                        onClick={() => {
                          if (sourceDetails.isError) sourceDetails.refetch();
                          if (dropdowns.isError) dropdowns.refetch();
                        }}
                      >
                        Retry
                      </Button>
                    }
                  >
                    {sourceDetails.isError
                      ? 'Unable to load the source cube subject. Please try again.'
                      : 'Unable to load cube options. Please try again.'}
                  </Alert>
                )}
                {renderDropdownField(fields[0])}
                {sourcePending && (
                  <Stack direction='row' spacing={1} alignItems='center' role='status'>
                    <CircularProgress size={16} aria-label='Loading source cube' />
                    <Typography variant='body2'>Loading source cube...</Typography>
                  </Stack>
                )}
                {source && !sourcePending && (
                  <CubeCurrentStructure
                    cube={source}
                    subjectName={cubes.find((cube) => cube.id === source.id)?.subjectDisplayName}
                    titleId='copy-current-structure-title'
                  />
                )}
                <Stack spacing={0.75}>
                  <Typography component='label' htmlFor='copy-cube-cubeName' variant='input-label' fontWeight='bold'>
                    Cube Name (optional)
                  </Typography>
                  <Typography id='copy-cube-cubeName-description' variant='input-label-gray' component='p'>
                    Selecting a source fills in its name followed by “Copy”. You can edit this name or leave it blank to
                    use the default.
                  </Typography>
                  <TextField
                    id='copy-cube-cubeName'
                    name='cubeName'
                    size='small'
                    fullWidth
                    placeholder='Revenue Forecast 2027'
                    value={formik.values.cubeName}
                    onChange={(event) => {
                      copy.reset();
                      formik.handleChange(event);
                    }}
                    onBlur={formik.handleBlur}
                    disabled={isSaving}
                    error={Boolean(formik.touched.cubeName && formik.errors.cubeName)}
                    helperText={formik.touched.cubeName && formik.errors.cubeName}
                    inputProps={{
                      maxLength: 255,
                      'aria-describedby': `copy-cube-cubeName-description${
                        formik.touched.cubeName && formik.errors.cubeName ? ' copy-cube-cubeName-helper-text' : ''
                      }`,
                    }}
                  />
                </Stack>
                {fields.slice(1).map(renderDropdownField)}
                <Stack spacing={1} sx={{ borderTop: '1px solid #E3EBF6', pt: 2 }}>
                  <FormControlLabel
                    sx={{ alignSelf: 'flex-start', ml: -1 }}
                    control={
                      <Checkbox
                        size='small'
                        checked={copyData}
                        onChange={(_event, checked) => setCopyData(checked)}
                        disabled={isSaving}
                        sx={{ color: '#98A2AE' }}
                        inputProps={{
                          'aria-describedby': 'copy-cube-data-description',
                        }}
                      />
                    }
                    label={
                      <Typography variant='input-label' fontWeight='bold'>
                        Copy the data
                      </Typography>
                    }
                  />
                  <Typography id='copy-cube-data-description' variant='input-label-gray' component='p'>
                    Copy matching data in the first DEV tenant, or in the CUSTOMER tenant if it is the only tenant in
                    the system. Initial values stay in their original matching period.
                  </Typography>
                  {copyData && (
                    <Stack spacing={1} aria-live='polite'>
                      {!canCheckData ? (
                        <Typography variant='body2' color='text.secondary'>
                          Complete the required fields to check the data mapping.
                        </Typography>
                      ) : isCheckingData ? (
                        <Stack direction='row' spacing={1} alignItems='center' role='status'>
                          <CircularProgress size={16} aria-label='Checking data mapping' />
                          <Typography variant='body2'>Checking the data mapping...</Typography>
                        </Stack>
                      ) : dataCheck.isError ? (
                        <Alert
                          severity='error'
                          action={
                            <Button color='inherit' size='small' onClick={() => dataCheck.refetch()}>
                              Retry
                            </Button>
                          }
                        >
                          {dataCheck.error?.response?.data?.message ||
                            'Unable to check the data mapping. Please try again.'}
                        </Alert>
                      ) : dataCheckResult ? (
                        <>
                          <Typography variant='body2' color='text.secondary'>
                            Checked in {dataCheckResult.tenantName} ({dataCheckResult.tenantType})
                          </Typography>
                          <Alert severity={dataCheckResult.hasMatchingStructure ? 'success' : 'warning'}>
                            <Stack spacing={0.5}>
                              {!dataCheckResult.hasMatchingStructure && (
                                <Typography variant='body2' fontWeight='bold'>
                                  No matching data structure. Review your selections.
                                </Typography>
                              )}
                              {dataCheckResult.summary.map((message, index) => (
                                <Typography key={index} variant='body2' fontWeight='bold'>
                                  {message}
                                </Typography>
                              ))}
                            </Stack>
                          </Alert>
                          {dataCheckResult.warnings.length > 0 && (
                            <Alert severity='warning'>
                              <Stack spacing={0.5}>
                                {dataCheckResult.warnings.map((message, index) => (
                                  <Typography key={index} variant='body2'>
                                    {message}
                                  </Typography>
                                ))}
                              </Stack>
                            </Alert>
                          )}
                        </>
                      ) : null}
                    </Stack>
                  )}
                </Stack>
              </>
            )}
          </Stack>
        </DialogContent>

        <Stack direction='row' justifyContent='flex-end' spacing={2} sx={{ px: 2, py: 2 }}>
          <Button variant='main-table-panel-border' onClick={handleClose} disabled={isSaving}>
            {copiedCube ? 'Close' : 'Cancel'}
          </Button>
          {copiedCube?.dataCopy?.status === 'INCOMPLETE' && (
            <ButtonLoading
              variant='main-table-panel'
              loading={retryData.isLoading}
              disabled={isSaving}
              onClick={handleRetryData}
            >
              Retry Data Copy
            </ButtonLoading>
          )}
          {copiedCube ? (
            <Button disabled={isSaving} variant='main-table-panel' onClick={handleOpenCopiedCube}>
              Open Copied Cube
            </Button>
          ) : (
            <ButtonLoading
              type='submit'
              variant='main-table-panel'
              loading={copy.isLoading}
              disabled={!formik.isValid || !canCopy || !isDataCheckReady}
            >
              Copy Cube
            </ButtonLoading>
          )}
        </Stack>
      </Stack>
    </Dialog>
  );
}
